import type { ErrorCopy } from '@shared/errors/catalog';

/**
 * The `error_code` values the four content surfaces meet, kept in one place
 * because all four talk to the same two services and three of them raise the
 * same codes.
 *
 * **Never inline a backend code in JSX.** The code is a log key; what reaches
 * the operator is the sentence, and the `X-Request-ID` is what they quote.
 *
 * Tone is load-bearing. An upstream failure nobody in this console caused is
 * **neutral**; `warning` marks a refusal the operator can act on; `danger` is
 * reserved for a destructive action that actually failed.
 */

export const contentErrors: Readonly<Record<string, ErrorCopy>> = {
  CONTENT_FORBIDDEN: {
    message: 'هذا الإجراء خارج صلاحيّتك.',
    tone: 'warning',
  },
  CONTENT_ITEM_NOT_FOUND: {
    message: 'لم نعثر على هذا العنوان. قد يكون حُذف بينما كانت الصفحة مفتوحة.',
    tone: 'warning',
  },
  CONTENT_NOT_FOUND: {
    message: 'لم نعثر على العنصر المطلوب.',
    tone: 'warning',
  },
  CONTENT_GROUP_NOT_FOUND: {
    message: 'لم نعثر على هذا الموسم. أعد تحميل الصفحة.',
    tone: 'warning',
  },
  CONTENT_MEDIA_ASSET_NOT_FOUND: {
    message: 'لم نعثر على هذا الملفّ. أعد تحميل الصفحة.',
    tone: 'warning',
  },
  /**
   * **The one code both a form and a fieldless action can raise**, so its copy
   * may not assume a form. It read «راجع الحقول المعلَّمة ثمّ أعد الإرسال» —
   * sound advice under an authoring form, and nonsense in the toast that
   * answered a failed *delete*, an action with no fields to review and nothing
   * marked. An operator sent looking for a marked field that does not exist
   * learns the console is guessing.
   *
   * The wording names what actually happened — the request was refused for what
   * it carried — and leaves the recovery to the shape the failure appeared in.
   */
  CONTENT_BAD_INPUT: {
    message: 'رفضت الخدمة هذا الطلب. أعد تحميل الصفحة ثمّ أعد المحاولة.',
    tone: 'warning',
  },
  /**
   * The row moved between load and click. The toast states it, the query is
   * invalidated, and the row re-renders in its true state — which also
   * re-derives what its menu offers.
   */
  CONTENT_INVALID_STATE_TRANSITION: {
    message: 'تغيّرت حالة العنوان بينما كانت الصفحة مفتوحة. حُدِّثت القائمة — راجع الحالة ثمّ أعد المحاولة.',
    tone: 'warning',
  },
  /**
   * Publication refused for missing required fields. `details[]` names each one,
   * so the surface lists them rather than repeating this sentence.
   */
  CONTENT_REQUIRED_METADATA_MISSING: {
    message: 'لا يمكن النشر قبل استكمال الحقول المطلوبة.',
    tone: 'warning',
  },
  /**
   * **The publish refusal a full form cannot answer.** Every required field can
   * be filled and the title still refused: `content` gates publication on
   * `asset_ready`, which `backend-assets` writes back only once a rung of the
   * ladder has finished encoding. Nothing here named the code, so
   * `resolveErrorCopy` fell through to `STATUS_FALLBACK[422]` — «راجع الحقول
   * المعلَّمة ثمّ أعد الإرسال» — under an action that marks no field and has
   * none to mark. An operator whose 2160p master was still transcoding was sent
   * to hunt for a marked field that does not exist.
   *
   * The sentence names the state and the wait, because the operator's next step
   * is neither to edit nor to retry: it is to let the encode finish. The assets
   * page states the same fact per file, on the row's head badge.
   */
  CONTENT_NO_PLAYABLE_RENDITION: {
    message:
      'لا يمكن النشر قبل جهوز نسخة قابلة للتشغيل. ما زال تحويل الملفّ جارياً — راجع صفحة الأصول لحالته.',
    tone: 'warning',
  },
  /**
   * **The one guard on removal.** Removing the only asset under a published
   * title would leave a published page with nothing to play, so the copy names
   * the way out rather than only the refusal.
   */
  CONTENT_LAST_ASSET_OF_PUBLISHED_ITEM: {
    message: 'هذا الملفّ الوحيد تحت عنوانٍ منشور. ألغِ النشر من قائمة المحتوى أوّلاً، ثمّ احذفه.',
    tone: 'warning',
  },
  /** A film has zero groups, and the service says so rather than accepting one. */
  CONTENT_GROUP_NOT_PERMITTED: {
    message: 'الفيلم ملفٌّ واحد بلا مواسم، فلا موسم يُنشأ عليه.',
    tone: 'warning',
  },
  CONTENT_METADATA_SCHEMA_INVALID: {
    message: 'البيانات الوصفية لا تطابق ما يقبله هذا النوع. راجع سنة الإنتاج وبلد الإنتاج.',
    tone: 'warning',
  },
  CONTENT_CREDIT_ROLE_NOT_CONFIGURED: {
    message: 'هذا الدور غير مُعرَّف. اختر دوراً من القائمة.',
    tone: 'warning',
  },
  CONTENT_SLUG_CONFLICT: {
    message: 'هذا المعرّف مستخدم بالفعل. اختر اسماً آخر.',
    tone: 'warning',
  },
  CONTENT_CONFLICT: {
    message: 'تغيّرت حالة العنصر بينما كانت الصفحة مفتوحة. أعد التحميل ثمّ أعد المحاولة.',
    tone: 'warning',
  },
  CONTENT_RATE_LIMITED: {
    message: 'طلبات كثيرة في وقت قصير. انتظر قليلاً ثمّ أعد المحاولة.',
    tone: 'warning',
  },
  /**
   * Enrichment is an accelerator, never a dependency for publication — and both
   * provider failures say so, because the operator's next step is to fill the
   * form by hand rather than to wait.
   */
  CONTENT_PROVIDER_RATE_LIMITED: {
    message: 'تجاوزنا حدّ الاستعلام من المزوّد. املأ الحقول بنفسك، أو أعد المحاولة بعد قليل.',
    tone: 'neutral',
  },
  CONTENT_PROVIDER_LOOKUP_FAILED: {
    message: 'لم يجب المزوّد. املأ الحقول بنفسك — التعبئة تسريعٌ لا شرط للنشر.',
    tone: 'neutral',
  },
  CONTENT_PROVIDER_TIMEOUT: {
    message: 'تأخّر المزوّد عن الردّ. املأ الحقول بنفسك — التعبئة تسريعٌ لا شرط للنشر.',
    tone: 'neutral',
  },
  CONTENT_UPSTREAM_FAILED: {
    message: 'الخدمة غير متاحة الآن. أعد المحاولة بعد قليل.',
    tone: 'neutral',
  },
  CONTENT_UPSTREAM_TIMEOUT: {
    message: 'تأخّرت الخدمة عن الردّ. أعد تحميل الصفحة للاطّلاع على النتيجة.',
    tone: 'neutral',
  },
};

/**
 * The `backend-assets` codes the assets surface meets.
 *
 * `ASSETS_PREVIEW_LIMIT_REACHED` is the one that needs its bound named: the
 * operator marked more assets free than `MAX_PREVIEW_ASSETS` permits, and the
 * sentence that helps is which of the two facts to change.
 */
export const assetsErrors: Readonly<Record<string, ErrorCopy>> = {
  ASSETS_FORBIDDEN: {
    message: 'هذا الإجراء خارج صلاحيّتك.',
    tone: 'warning',
  },
  ASSETS_NOT_FOUND: {
    message: 'لم نعثر على هذا الملفّ. أعد تحميل الصفحة.',
    tone: 'warning',
  },
  ASSETS_BAD_INPUT: {
    message: 'تعذّر إتمام الطلب بهذا الملفّ. راجعه ثمّ أعد المحاولة.',
    tone: 'warning',
  },
  ASSETS_CONFLICT: {
    message: 'يوجد مسار بهذه اللغة على هذا الملفّ بالفعل.',
    tone: 'warning',
  },
  ASSETS_PREVIEW_LIMIT_REACHED: {
    message: 'بلغ العنوان أقصى عدد من الملفّات المجّانية. ارفع المجّانيّة عن ملفٍّ آخر أوّلاً.',
    tone: 'warning',
  },
  /**
   * **The refusal the cover control met and could not say.** `backend-assets`
   * checks the source against the minimum for every REQUIRED ratio of the
   * subject — `CONTENT_ITEM.thumbnail` is `2:3` at `1000×1500` — and answers
   * `422` with the floor and the purpose in `data`. Nothing here named the
   * code, so `resolveErrorCopy` fell through to `STATUS_FALLBACK[422]`:
   * «راجع الحقول المعلَّمة ثمّ أعد الإرسال». Under an image upload that names
   * no field, and no field is marked — an operator whose 1920×1080 file was
   * refused for being under the 2:3 floor was sent to review fields that do
   * not exist. The refusal reached the console all along; what it lacked was
   * a sentence.
   *
   * The numbers stay OFF this line and on the hint under the upload zone,
   * which reads them from `/uploadPolicy` and states them before the file is
   * transferred rather than after. Same wording the editorial surface ships.
   */
  ASSETS_ARTWORK_TOO_SMALL: {
    message: 'أبعاد الصورة أقلّ من الحدّ الأدنى المذكور تحت منطقة الرفع. اختر صورةً أكبر.',
    tone: 'warning',
  },
  ASSETS_UNSUPPORTED_FORMAT: {
    message: 'هذه الصيغة غير مقبولة. راجع الصيغ المذكورة تحت منطقة الرفع.',
    tone: 'warning',
  },
  ASSETS_UPLOAD_TOO_LARGE: {
    message: 'الملفّ أكبر من الحدّ المسموح. راجع الحدّ المذكور تحت منطقة الرفع.',
    tone: 'warning',
  },
  ASSETS_SOURCE_OBJECT_MISSING: {
    message: 'لم يصل الملفّ إلى التخزين. أعد الرفع.',
    tone: 'warning',
  },
  ASSETS_PLACEMENT_UNKNOWN: {
    message: 'لم نعثر على موضع هذا الملفّ. أعد تحميل الصفحة ثمّ أعد الرفع.',
    tone: 'warning',
  },
  ASSETS_RATE_LIMITED: {
    message: 'رفعٌ كثير في وقت قصير. انتظر قليلاً ثمّ أعد المحاولة.',
    tone: 'warning',
  },
  ASSETS_UPSTREAM_FAILED: {
    message: 'الخدمة غير متاحة الآن. أعد المحاولة بعد قليل.',
    tone: 'neutral',
  },
  ASSETS_UPSTREAM_TIMEOUT: {
    message: 'تأخّرت الخدمة عن الردّ. أعد تحميل الصفحة للاطّلاع على النتيجة.',
    tone: 'neutral',
  },
};

/** The two catalogues together, for the surfaces that read both services. */
export const contentAndAssetsErrors: Readonly<Record<string, ErrorCopy>> = {
  ...contentErrors,
  ...assetsErrors,
};
